import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";

import { request } from "@/api/client";
import { resolveApiUrl } from "@/lib/api";

export type AgentChatMessage = {
  id: string;
  sender: string;
  recipient: string;
  payload?: Record<string, unknown> | null;
  type?: string | null;
  timestamp?: string | null;
  content?: string | null;
  /** Derived from sender: "user" for human inputs, "agent" for all agent notes */
  role: "user" | "agent";
  /** True for messages added optimistically before the server confirms */
  optimistic?: boolean;
};

const USER_SENDERS = new Set(["user", "human", "human_input"]);

function deriveRole(sender: string): "user" | "agent" {
  return USER_SENDERS.has(sender.toLowerCase()) ? "user" : "agent";
}

type ConversationThread = {
  id: string;
  participants: string[];
  messages: AgentChatMessage[];
};

export function useAgentChat(agentName: string) {
  const storageKey = `agents.chat.threadId.${agentName}`;
  const [searchParams, setSearchParams] = useSearchParams();

  // URL param is the canonical source of truth; localStorage is the fallback for first load
  const urlThreadId = searchParams.get("thread");

  const [localThreadId, setLocalThreadId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(storageKey) || null;
  });

  const threadId = urlThreadId ?? localThreadId;

  // Optimistic user messages that haven't been confirmed by the server yet
  const [optimisticMessages, setOptimisticMessages] = useState<AgentChatMessage[]>([]);

  // On mount, sync a stored localStorage thread into the URL if the URL has none
  useEffect(() => {
    if (!urlThreadId && localThreadId) {
      setSearchParams(
        (prev) => { const p = new URLSearchParams(prev); p.set("thread", localThreadId); return p; },
        { replace: true },
      );
    }
    // intentionally only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setThreadId = useCallback(
    (id: string | null) => {
      setLocalThreadId(id);
      if (id) {
        window.localStorage.setItem(storageKey, id);
        setSearchParams(
          (prev) => { const p = new URLSearchParams(prev); p.set("thread", id); return p; },
          { replace: true },
        );
      } else {
        window.localStorage.removeItem(storageKey);
        setSearchParams(
          (prev) => { const p = new URLSearchParams(prev); p.delete("thread"); return p; },
          { replace: true },
        );
      }
    },
    [storageKey, setSearchParams],
  );

  const { data, error, isLoading, mutate } = useSWR<ConversationThread | null>(
    threadId ? ["agent-conversation", threadId] : null,
    () => request<ConversationThread>(`/agents/conversations/${encodeURIComponent(threadId || "")}`),
    // SSE handles real-time updates; polling is a fallback safety net
    { refreshInterval: 15_000 },
  );

  // Immediately refetch when thread changes
  useEffect(() => {
    if (threadId === null) return;
    void mutate();
  }, [mutate, threadId]);

  // SSE subscription: trigger immediate refetch on thread_update events for our thread
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  useEffect(() => {
    if (!threadId) return;

    const url = resolveApiUrl("/stream");
    let es: EventSource;

    try {
      es = new EventSource(url);
    } catch {
      // EventSource not supported or URL invalid — polling fallback handles it
      return;
    }

    const handler = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as { thread?: string; agent?: string };
        if (data.thread && data.thread === threadIdRef.current) {
          void mutateRef.current();
        }
      } catch {
        // ignore malformed payloads
      }
    };

    es.addEventListener("thread_update", handler);

    return () => {
      es.removeEventListener("thread_update", handler);
      es.close();
    };
  }, [threadId]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return null;

      // Add optimistic user message immediately so the UI responds before the server
      const optimisticId = `optimistic-${Date.now()}`;
      const optimisticMsg: AgentChatMessage = {
        id: optimisticId,
        sender: "user",
        recipient: agentName,
        content,
        type: "context",
        timestamp: new Date().toISOString(),
        role: "user",
        optimistic: true,
      };
      setOptimisticMessages((prev) => [...prev, optimisticMsg]);

      try {
        const response = await request<{ thread_id: string }>(`/agents/${encodeURIComponent(agentName)}/threads/start`, {
          method: "POST",
          body: { context: content, thread_id: threadId ?? undefined },
        });
        if (response?.thread_id) {
          setThreadId(response.thread_id);
        }
        await mutate();
        // Remove the optimistic message once the server has confirmed
        setOptimisticMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        return response;
      } catch (err) {
        // On error, remove the optimistic message and re-throw
        setOptimisticMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        throw err;
      }
    },
    [agentName, mutate, threadId, setThreadId],
  );

  const stopThread = useCallback(
    async (id?: string | null) => {
      const target = id ?? threadId;
      if (!target) return;
      await request(`/agents/threads/${encodeURIComponent(target)}/stop`, { method: "POST" });
      await mutate();
    },
    [threadId, mutate],
  );

  // Derive server messages with role
  const serverMessages = useMemo(
    () => (data?.messages ?? []).map((m) => ({ ...m, role: deriveRole(m.sender) })),
    [data],
  );

  // Merge server messages with optimistic ones, deduplicating by content + role
  const messages = useMemo((): AgentChatMessage[] => {
    if (optimisticMessages.length === 0) return serverMessages;
    // Drop optimistic messages whose content already appears as a confirmed "user" server message
    const confirmedUserContent = new Set(
      serverMessages.filter((m) => m.role === "user").map((m) => m.content ?? ""),
    );
    const pendingOptimistic = optimisticMessages.filter(
      (m) => !confirmedUserContent.has(m.content ?? ""),
    );
    return [...serverMessages, ...pendingOptimistic];
  }, [serverMessages, optimisticMessages]);

  return {
    threadId,
    messages,
    participants: data?.participants ?? [],
    loading: isLoading,
    error,
    sendMessage,
    stopThread,
    setThreadId,
    refresh: mutate,
  };
}
